import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useAppDispatch,
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'

import { getSelectedPolicy } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PolicySelector from './PolicySelector'
import useClaims from '@verzekeringapp-mono/shared/src/hooks/use_claims'
import {
  setClaimDamageForm,
  setCurrentStep,
  setIsFormCompleted
} from '@verzekeringapp-mono/shared/src/state/slices/claims_slice'
import {
  getClaimDamageForm,
  getClaimIsLoading,
  getConceptClaim,
  getConceptIsLoading,
  getFileIsLoading
} from '@verzekeringapp-mono/shared/src/state/selectors/claims'
import { format, parse } from 'date-fns'
import formatPhoneNumber from '@verzekeringapp-mono/shared/src/utils/formatPhoneNumber'
import formatToApiDate from '@verzekeringapp-mono/shared/src/utils/formatToApiDate'
import Button from '~/components/Button'
import LoadingIndicator from '~/components/LoadingIndicator'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import DatePicker from 'react-datepicker'

const getControlClassName = (
  hasError: boolean,
  extraClassName = ''
) => [
  'w-full rounded-md border bg-surface-input p-4 text-text-primary placeholder:text-text-placeholder shadow-sm focus:outline-none',
  hasError
    ? 'border-border-danger focus:border-border-danger'
    : 'border-border-default focus:border-border-focus',
  extraClassName
].filter(Boolean).join(' ')

const ClaimDamageForm = () => {
  const selectedPolicy = useAppSelector(getSelectedPolicy)

  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const claimDamageForm = useAppSelector(getClaimDamageForm)
  const conceptIsLoading = useAppSelector(getConceptIsLoading)
  const claimIsLoading = useAppSelector(getClaimIsLoading)
  const fileIsLoading = useAppSelector(getFileIsLoading)

  const [isAttachmentInfoOpen, setIsAttachmentInfoOpen] = React.useState(false)
  const [isConceptSaved, setIsConceptSaved] = React.useState(false)
  const [errorKey] = React.useState(0)
  const conceptClaim = useAppSelector(getConceptClaim)
  const datePickerRef = useRef<DatePicker>(null)

  const maxDate = useMemo(() => {
    const today = new Date()

    if (selectedPolicy?.maximumDateOfLoss) {
      return selectedPolicy.maximumDateOfLoss
        ? new Date(
          Math.min(
            new Date(selectedPolicy.maximumDateOfLoss).getTime(),
            today.getTime()
          )
        )
        : today
    }

    return today
  }, [selectedPolicy])

  const {
    createClaimConcept,
    uploadClaimAttachmentFiles,
    deleteClaimAttachment,
    updateClaimConcept,
    uploadErrors
  } = useClaims()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
    setValue,
    watch,
    control
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      date: formatToApiDate(claimDamageForm.date),
      description: claimDamageForm.description,
      phone: claimDamageForm.phoneNumber?.startsWith('06')
        ? claimDamageForm.phoneNumber?.split('06')[1]
        : claimDamageForm.phoneNumber,
      attachments: claimDamageForm.attachments
    }
  })

  const description = watch('description')

  const dispatch = useAppDispatch()

  const onSubmit = () => {
    reset()
    dispatch(setIsFormCompleted(true))
    dispatch(setCurrentStep(2))
  }

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files ?? [])

        if (!conceptClaim) {
          const concept = await handleSaveConcept()

          if (!concept) {
            alert (
              'Er is iets misgegaan bij het opslaan van het concept. Probeer het opnieuw.'
            )

            return
          }

          uploadClaimAttachmentFiles(concept?.id ?? '', files)

          return
        }

        uploadClaimAttachmentFiles(conceptClaim?.id ?? '', files)
      }
    },
    [conceptClaim]
  )

  useEffect(() => {
    if (errors.date || errors.description || errors.phone || isDirty) {
      dispatch(setIsFormCompleted(false))
    }
  }, [errors])

  const getValidatedDate = useCallback(
    (dateString?: string, policy?: typeof selectedPolicy) => {
      if (!dateString) return new Date()

      const date = parse(dateString, 'yyyy-MM-dd', new Date())

      if (policy) {
        const minDate = new Date(policy.minimumDateOfLoss)

        const maxDate = policy.maximumDateOfLoss
          ? new Date(policy.maximumDateOfLoss)
          : new Date()

        const dateToCheck = new Date(date)

        // Include the maximum and minimum date itself
        minDate.setHours(0, 0, 0, 0)
        maxDate.setHours(23, 59, 59, 999)
        dateToCheck.setHours(12, 0, 0, 0)

        if (dateToCheck < minDate || dateToCheck > maxDate) {
          return null
        }
      }

      return date
    },
    []
  )

  const handleCreateConcept = useCallback(async (date: Date) => {
    if (!selectedPolicy) return null

    let claim = await createClaimConcept({
      policyNumber: selectedPolicy.policyNumber,
      agentNumber: resourceAuthorization?.relationInfo?.agentNumber ?? '',
      relationNumber: Number(resourceAuthorization?.relationInfo?.relationNumber),
      branche: selectedPolicy.branche
    })

    if (claim) {
      claim = await updateClaimConcept({
        ...claim,
        dateOfLoss: date ? format(date, 'yyyy-MM-dd') : '',
        text: claimDamageForm.description || '',
        phoneNumber: claimDamageForm.phoneNumber
          ? formatPhoneNumber(claimDamageForm.phoneNumber)
          : ''
      })
    }

    return claim
  }, [selectedPolicy, resourceAuthorization, claimDamageForm, createClaimConcept, updateClaimConcept])

  const handleSaveConcept = async () => {
    const formData = getValues()
    let claim = null

    const date = getValidatedDate(formData.date, selectedPolicy)

    if (conceptClaim) {
      claim = await updateClaimConcept({
        ...conceptClaim,
        policyNumber: selectedPolicy?.policyNumber ?? claimDamageForm.policyId,
        dateOfLoss: date ? format(date, 'yyyy-MM-dd') : '',
        text: claimDamageForm.description || '',
        phoneNumber: claimDamageForm.phoneNumber
          ? formatPhoneNumber(claimDamageForm.phoneNumber)
          : ''
      })
    } else if (date) {
      claim = await handleCreateConcept(date)
    }

    setIsConceptSaved(true)
    reset(formData)

    return claim
  }

  const onChangeForm = useCallback(() => {
    if (!selectedPolicy) return

    const values = getValues()

    dispatch(
      setClaimDamageForm({
        policyId: selectedPolicy.policyNumber,
        date: values.date
          ? format(new Date(values.date), 'dd-MM-yyyy')
          : conceptClaim?.dateOfLoss
            ? format(new Date(conceptClaim.dateOfLoss), 'dd-MM-yyyy')
            : '',
        description: values.description || conceptClaim?.text || '',
        phoneNumber: values.phone || conceptClaim?.phoneNumber || '',
        attachments: values.attachments ?? conceptClaim?.files
      })
    )
  }, [selectedPolicy, conceptClaim])

  useEffect(() => {
    const { date } = getValues()

    // Clear date if out of bounds
    if (selectedPolicy && date !== '' && date !== undefined && date !== null) {
      const dateToCheck = parse(date, 'yyyy-MM-dd', new Date())
      const minDate = new Date(selectedPolicy.minimumDateOfLoss)

      const maxDate = selectedPolicy.maximumDateOfLoss
        ? new Date(selectedPolicy.maximumDateOfLoss)
        : new Date()

      // Include the maximum and minimum date itself
      minDate.setHours(0, 0, 0, 0)
      maxDate.setHours(23, 59, 59, 999)
      dateToCheck.setHours(12, 0, 0, 0)

      if (dateToCheck < minDate || dateToCheck > maxDate) {
        setValue('date', '')
      }
    }
  }, [selectedPolicy, maxDate])

  const onSubmitError = useCallback(() => {

    if (errors.date) {
      datePickerRef.current?.setFocus()
    }
  }, [errors])

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onSubmitError)}
      onChange={onChangeForm}
      className='mx-auto w-full gap-2 rounded-lg border border-border-subtle bg-surface-card px-8 py-4 md:p-8 lg:w-5/6'
    >
      <div className='mb-8'>
        <h2
          aria-label='Schadevragen'
          className='text-3xl font-medium mb-4'
        >Algemeen</h2>

        <p className='text-text-secondary'>
          Om te bepalen wat voor soort schade je hebt opgelopen, zijn er enkele
          vragen noodzakelijk.
        </p>
      </div>

      <div className='flex flex-1 gap-4 flex-col md:flex-row'>
        <PolicySelector />

        <div className='mb-6 flex flex-col flex-1'>
          <label
            htmlFor='date'
            className='mb-2 w-full font-medium text-text-primary'
          >
            Wat is de datum waarop de schade is ontstaan?
          </label>

          <div className='w-full flex-1'>
            <Controller
              name='date'
              control={control}
              rules={{
                required: 'Dit veld is verplicht.'
              }}
              render={({ field }) => (
                <DatePicker
                  ref={datePickerRef}
                  locale='nl'
                  aria-describedby={errors.date ? 'date-error' : undefined}
                  id='date'
                  wrapperClassName='w-full'
                  selected={field.value ? parse(field.value, 'yyyy-MM-dd', new Date()) : null}
                  onChange={(date: Date | null) => {
                    const formattedDate = date ? format(date, 'yyyy-MM-dd') : ''

                    field.onChange(formattedDate)
                    onChangeForm()
                  }}

                  minDate={
                    selectedPolicy?.minimumDateOfLoss
                      ? new Date(selectedPolicy.minimumDateOfLoss)
                      : undefined
                  }
                  maxDate={maxDate}
                  dateFormat='dd/MM/yyyy'
                  placeholderText={format(new Date(), 'dd/MM/yyyy')}
                  className={getControlClassName(!!errors.date)}
                />
              )}
            />

            {errors.date && (
              <span
                role='alert'
                className='mt-1 block text-sm text-status-danger-foreground'
              >
                {errors.date.message}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className='mb-6 flex flex-col '>
        <div className='flex items-center justify-between w-full'>
          <label
            htmlFor='description'
            className='mb-2 w-full font-medium text-text-primary'
          >
            Geef een gedetailleerde omschrijving van de schade
          </label>

          {description && (
            <span className='hidden text-text-muted md:block'>
              {600 - description.length}
            </span>
          )}
        </div>

        <textarea
          id='description'
          {...register('description', { required: true, maxLength: 600 })}
          rows={4}
          placeholder='Hoe is de schade ontstaan, waar is de schade ontstaan, waaraan is schade, door wie is de schade ontstaan etc...'
          maxLength={600}
          className={`w-full border ${
            errors.description ? 'border-red' : 'border-lightGray'
          } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 p-4 text-gray`}
          aria-describedby={errors.description ? 'description-error' : undefined}
        />

        {errors.description && (
          <span
            id='description-error'
            role='alert'
            className='mt-1 block text-sm text-status-danger-foreground'
          >
            Dit veld is verplicht.
          </span>
        )}
      </div>

      <div className='mb-6 flex flex-col '>
        <label
          htmlFor='phone'
          className='mb-2 w-full font-medium text-text-primary'
        >
          Wat is je telefoonnummer?
        </label>

        <div className='w-full flex'>
          <span className='rounded-bl-md rounded-tl-md border border-border-default bg-surface-info p-4 text-text-secondary shadow-sm'>
            06
          </span>

          <input
            id='phone'
            type='tel'
            maxLength={8}
            autoComplete='tel-local'
            {...register('phone', {
              required: 'Dit veld is verplicht.',
              pattern: {
                value: /^[0-9]{8}$/,
                message: 'Voer een geldig telefoonnummer in.'
              }
            })}
            className={getControlClassName(
              !!errors.phone,
              'rounded-br-md rounded-tr-md rounded-bl-none rounded-tl-none border-l-0'
            )}
            placeholder='12345678'
            aria-describedby={errors.phone ? 'phone-error' : undefined}
          />
        </div>

        {errors.phone && (
          <span
            key={`phone-error-${errorKey}`}
            id='phone-error'
            role='alert'
            className='mt-1 block text-sm text-status-danger-foreground'
          >
            {errors.phone.message}
          </span>
        )}
      </div>

      <div className='mb-6 flex flex-col '>
        <div>
          <label
            htmlFor='attachments'
            className='mb-2 w-full font-medium text-text-primary'
            role='button'
            aria-label='info over ondersteunde bestandtypes'
          >
            Voeg bijlage(n) toe
          </label>

          <FontAwesomeIcon
            icon={faInfoCircle}
            size='lg'
            className='ml-2 cursor-pointer text-icon-secondary'
            onClick={() => setIsAttachmentInfoOpen(!isAttachmentInfoOpen)}
            aria-label='Informatie over ondersteunde bestandtypes'
          />
        </div>

        {isAttachmentInfoOpen && (
          <div className='my-4 rounded-lg bg-surface-info p-4'>
            <p className='text-text-secondary'>
              Bestandstypen die worden ondersteund: eml, msg, pdf, doc, docx, xls, xlsx, txt, avi, mov, mpeg, mp4, jpeg, jpg, png, bmp, tif, tiff, heic
            </p>
          </div>
        )}

        <p className='my-2 text-text-secondary'>
          Denk aan kleurenfoto&apos;s van de schade, offertes, een politierapport en aankoopnota&apos;s.
        </p>

        <div className='flex flex-col gap-4'>
          {conceptClaim?.files?.map(file => (
            <div
              key={file.url}
              className='flex w-full items-center justify-between rounded-lg border border-border-brand bg-surface-brand-subtle px-4 py-4 font-medium text-text-primary'
            >
              <span className='text-text-brand'>{file.title}</span>

              <button
                onClick={() => {
                  deleteClaimAttachment(file.url, conceptClaim.id)
                }}
                type='button'
              >
                <FontAwesomeIcon
                  icon={'trash'}
                  className='mr-4 text-icon-brand'
                />
              </button>
            </div>
          ))}
        </div>

        <div className='w-full flex items-center mt-8'>
          {fileIsLoading
            ? (
              <div className='items-center text-action-primary-background'>
                <LoadingIndicator />
              </div>
            )
            : (
              <>
                <label
                  htmlFor='attachments'
                  className='flex w-full cursor-pointer items-center rounded-lg border border-border-brand bg-surface-brand-subtle px-6 py-4 font-medium text-text-primary'
                >
                  <FontAwesomeIcon
                    icon={'plus'}
                    className='mr-4 text-icon-brand'
                  />

                  <span className='text-text-brand'>Selecteer bestand(en)</span>
                </label>

                <input
                  id='attachments'
                  type='file'
                  onChange={onFileChange}
                  accept='image/jpg, image/jpeg, image/png, image/heic, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, video/*, text/*'
                  className='hidden'
                  multiple
                />
              </>
            )}
        </div>

        {
          uploadErrors.length > 0 && (
            <div
              role='alert'
              className='mt-4'
            >
              {uploadErrors.map((error) => (
                <p
                  key={error}
                  className='text-sm text-status-danger-foreground'
                >
                  {error}
                </p>
              ))}
            </div>
          )
        }
      </div>

      <div className='flex justify-between'>
        <Button
          onClick={() => {
            handleSaveConcept()
          }}
          variant='outline-brand'
          isLoading={conceptIsLoading}
          disabled={conceptIsLoading || fileIsLoading}
          replaceChildrenWhileLoading={true}
        >
          {isConceptSaved && !isDirty
            ? 'Concept opgeslagen'
            : 'Concept opslaan'}
        </Button>

        <Button
          type='submit'
          disabled={claimIsLoading || conceptIsLoading || fileIsLoading}
        >
          Volgende
          <FontAwesomeIcon
            icon={['fas', 'arrow-right']}
            className='ml-2'
          />
        </Button>
      </div>
    </form>
  )
}

export default ClaimDamageForm
